import React from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Volume2, Maximize2, Lightbulb, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const FindDeviceWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");

    return (
        <motion.div
            key="findDevice-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-violet-200 relative overflow-hidden">
                {/* background blobs */}
                <motion.div
                    className="absolute -right-10 top-10 w-32 h-32 bg-violet-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute -left-10 -bottom-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.7, 0.5] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                />

                {/* header */}
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-600"
                            animate={{ rotate: [0, 15, -15, 0] }}
                            transition={{ duration: 4, repeat: Infinity }}
                        >
                            <Search className="w-4 h-4" />
                        </motion.div>
                        <h5 className="text-violet-800 font-semibold text-base">{t("widgets.findDevice.title")}</h5>
                    </div>
                    <motion.span
                        className="text-xs bg-gradient-to-r from-violet-500 to-purple-600 text-white px-2.5 py-1 rounded-full font-medium"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }}
                    >
                        {t("widgets.findDevice.active")}
                    </motion.span>
                </div>

                {/* map area */}
                <motion.div
                    className="relative h-44 mb-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-100 border border-violet-200 shadow-inner overflow-hidden flex items-center justify-center"
                    whileHover={{ scale: 1.02 }}
                >
                    {/* radar rings */}
                    {[...Array(3)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full border-2 border-violet-400/50"
                            style={{ width: `${(i + 1) * 60}px`, height: `${(i + 1) * 60}px` }}
                            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0.1, 0.6] }}
                            transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.6 }}
                        />
                    ))}

                    {/* device pin */}
                    <motion.div
                        className="relative z-10 w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full border-2 border-white shadow-xl flex items-center justify-center cursor-pointer"
                        animate={{
                            scale: [1, 1.2, 1],
                            boxShadow: ["0 0 0 0 rgba(139, 92, 246, 0.7)", "0 0 0 12px rgba(139, 92, 246, 0)", "0 0 0 0 rgba(139, 92, 246, 0.7)"],
                        }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                        whileHover={{ scale: 1.3 }}
                    >
                        <MapPin className="text-white w-5 h-5" />
                    </motion.div>

                    {/* last location label */}
                    <motion.div
                        className="absolute bottom-2.5 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-3 py-1 rounded-full shadow-md text-xs flex items-center gap-1.5"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <MapPin className="w-3 h-3 text-violet-500" />
                        <span className="text-gray-700 font-medium">{t("widgets.findDevice.locationAddress")}</span>
                        <Maximize2 className="w-3 h-3 text-violet-400 cursor-pointer" />
                    </motion.div>
                </motion.div>

                {/* info cards */}
                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    <motion.div className="bg-violet-50 p-3 rounded-lg border border-violet-200" whileHover={{ scale: 1.03, y: -2 }}>
                        <div className="text-violet-700 font-medium mb-1">{t("widgets.findDevice.connectionStatus")}</div>
                        <div className="flex items-center gap-1.5">
                            <motion.span className="w-2 h-2 rounded-full bg-green-500" animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                            <span className="text-gray-600">{t("widgets.findDevice.online")}</span>
                        </div>
                    </motion.div>

                    <motion.div className="bg-purple-50 p-3 rounded-lg border border-purple-200" whileHover={{ scale: 1.03, y: -2 }}>
                        <div className="text-purple-700 font-medium mb-1">{t("widgets.findDevice.accuracy")}</div>
                        <div className="text-gray-600">{t("widgets.findDevice.accuracyValue")}</div>
                    </motion.div>
                </div>

                {/* search progress bar */}
                <div className="mb-3 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full"
                        animate={{ width: ["15%", "85%", "60%"] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>

                {/* play sound button */}
                <motion.button
                    className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white py-2.5 rounded-lg text-sm font-medium relative overflow-hidden"
                    whileHover={{ scale: 1.02, y: -2, boxShadow: "0 5px 15px rgba(139,92,246,0.4)" }}
                    whileTap={{ scale: 0.98 }}
                >
                    <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-violet-500/0 via-white/20 to-violet-500/0"
                        animate={{ x: ["-100%", "100%"] }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                            <Volume2 className="w-4 h-4" />
                        </motion.div>
                        {t("widgets.findDevice.playSound")}
                    </span>
                </motion.button>

                {/* tip */}
                <motion.div
                    className="mt-3 bg-violet-50 border border-violet-200 text-violet-700 text-xs p-2.5 rounded-lg flex items-center gap-2 relative overflow-hidden"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <Lightbulb className="w-4 h-4 flex-shrink-0" />
                    <span>{t("widgets.findDevice.tip")}</span>
                    <motion.button className="ml-auto bg-violet-100 rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0" whileHover={{ scale: 1.2 }}>
                        <X className="w-3 h-3" />
                    </motion.button>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default FindDeviceWidget;
